import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { CITIES, SERVICES_FR, SERVICES_EN } from '@/data/siteData';
import { MapPin, ArrowRight } from 'lucide-react';

const CityCard = ({ city }) => {
  const { lang, t } = useLanguage();
  const prefix = lang === 'en' ? '/en' : '';

  const cityLink = lang === 'en'
    ? `${prefix}/apartment-cleaning-${city.slug}`
    : `/nettoyage-appartement-${city.slug}`;

  return (
    <Link
      to={cityLink}
      className="group relative bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-stone-100 hover:border-[#D4AF37]/30 overflow-hidden"
      data-testid={`city-card-${city.id}`}
    >
      {/* Background Gradient on Hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#faf8f5] to-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <div className="relative">
        {/* Icon */}
        <div className="w-12 h-12 bg-[#1a365d] group-hover:bg-gradient-to-br group-hover:from-[#D4AF37] group-hover:to-[#C9A227] rounded-xl flex items-center justify-center mb-4 transition-all duration-300">
          <MapPin className="w-6 h-6 text-[#D4AF37] group-hover:text-white transition-colors duration-300" />
        </div>

        {/* City Name */}
        <h3 className="text-xl font-bold text-[#1a365d] mb-2 group-hover:text-[#D4AF37] transition-colors" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          {lang === 'en' ? city.nameEn : city.nameFr}
        </h3>

        {/* Services Available */}
        <p className="text-sm text-slate-500 mb-4">
          {t('Tous nos services disponibles', 'All our services available')}
        </p>

        {/* Arrow */}
        <div className="flex items-center gap-2 text-[#D4AF37] font-medium">
          {t('Voir les services', 'View services')}
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
};

const CitiesSection = () => {
  const { lang, t } = useLanguage();

  return (
    <section className="py-20 bg-[#faf8f5]" data-testid="cities-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase mb-4">
            {t('Nos Zones', 'Our Areas')}
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1a365d] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t("Zone d'Intervention", 'Service Areas')}
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            {t(
              "Nous Intervenons sur Toute la Côte d'Azur",
              'We Operate Throughout the French Riviera'
            )}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-[#D4AF37] to-[#C9A227] mx-auto mt-6 rounded-full" />
        </div>

        {/* Cities Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CITIES.map((city) => (
            <CityCard key={city.id} city={city} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CitiesSection;
